import * as React from 'react';

import { Collapse, IconButton, Tooltip, Typography } from '@mui/material';
import {
  Check,
  ChevronRight,
  CircleCheck,
  CircleX,
  Copy,
  Wifi,
  WifiOff,
} from 'lucide-react';

import { useLocale } from '@shared/hooks/locale';
import type { useTheme } from '@shared/theme/theme';

import { formatFileSize } from './artifactFiles';
import type {
  DetailRow,
  PresenterLabels,
  ReadableBlock,
} from './toolPresenters';

type Colors = ReturnType<typeof useTheme>['colors'];

export const usePresenterLabels = (): PresenterLabels => {
  const { t, intlLocale } = useLocale();
  // Literal t() calls so the translator-scanner can extract these keys.
  return React.useMemo(
    () => ({
      action: t('Action'),
      record: t('Record'),
      allRecords: t('All records'),
      andMore: t('and more'),
      more: count => t('+{{count}} more', { count }),
      changes: t('Changes'),
      create: t('Create'),
      update: t('Update'),
      delete: t('Delete'),
      yes: t('Yes'),
      no: t('No'),
      contains: t('contains'),
      notContains: t('does not contain'),
      isEmpty: t('is empty'),
      isNotEmpty: t('is not empty'),
      oneOf: t('is one of'),
      noneOf: t('is none of'),
      or: t('or'),
      lookedAt: t('Looked at'),
      allTables: t('All available tables'),
      availableChanges: t('Available changes'),
      playbook: t('Playbook'),
      playbookLoaded: t('Playbook loaded'),
      search: t('Search'),
      language: t('Language'),
      page: t('Page'),
      opened: t('Opened'),
      table: t('Table'),
      country: t('Country'),
      recordId: t('Record id'),
      filter: t('Filter'),
      sort: t('Sort'),
      descending: t('descending'),
      fields: t('Fields'),
      distinct: t('Distinct values of'),
      limit: t('Limit'),
      offset: t('Offset'),
      mail: {
        names: t('Names'),
        recordType: t('Record type'),
        domain: t('Domain'),
        priority: t('Priority'),
        mailServer: t('Mail server'),
        value: t('Value'),
        noMail: t('this domain does not receive email'),
        implicitMx: t('no MX record, mail goes to the domain address'),
        emailOrDomain: t('Email or domain'),
        nylasOption: t('Option to choose'),
        provider: t('Provider'),
        mailFilter: t('Mail filter'),
        imapServer: t('IMAP server'),
        smtpServer: t('SMTP server'),
        port: t('Port'),
        security: t('Security'),
        server: t('Server'),
        result: t('Result'),
        nylasOptions: {
          google: 'Google',
          microsoft: 'Microsoft',
          icloud: 'iCloud',
          yahoo: 'Yahoo',
          imap: 'IMAP',
        },
        securityModes: {
          ssl: 'SSL/TLS',
          starttls: 'STARTTLS',
          none: t('None'),
        },
        checks: {
          dns: t('Server name found'),
          connect: t('Connection'),
          tls: t('Certificate'),
          greeting: t('Server answer'),
          login_methods: t('Login methods'),
        },
      },
      file: t('File'),
      fileType: t('Type'),
      sandbox: {
        command: t('Command'),
        timeLimit: t('Time limit'),
        seconds: seconds => t('{{seconds}} s', { seconds }),
        online: t('Online'),
        offline: t('Offline'),
        onlineHint: t('Network is on: this chat has not accessed CRM data.'),
        offlineHint: t('Network is off because this chat accessed CRM data.'),
        stdout: t('Output'),
        stderr: t('Error output'),
        noOutput: t('No output'),
        truncated: t('The output was cut: only its end is shown.'),
        timedOut: t('The command was stopped: it ran past its time limit.'),
        path: t('Path'),
        size: t('Size'),
        fileSize: bytes => formatFileSize(bytes, intlLocale),
        busy: t(
          'The sandbox is busy: too many are running right now, including in your other chats. Try again in a minute.',
        ),
        fromSandbox: t('From the sandbox'),
        file: t('File'),
      },
    }),
    [t, intlLocale],
  );
};

const BlockTitle = ({ title, colors }: { title: string; colors: Colors }) => (
  <Typography
    variant="caption"
    sx={{ display: 'block', fontWeight: 600, color: colors.grey700 }}
  >
    {title}
  </Typography>
);

const RowsView = ({
  rows,
  colors,
}: {
  rows: Array<DetailRow>;
  colors: Colors;
}) => {
  const { t } = useLocale();
  return (
    // No column gap: `_or` rows shade both cells as one continuous band, so
    // the spacing between label and value is padding on the value cell.
    <div
      css={{
        display: 'grid',
        gridTemplateColumns: 'fit-content(40%) minmax(0, 1fr)',
        rowGap: 2,
        fontSize: '0.8125rem',
        lineHeight: 1.5,
      }}
    >
      {rows.map((row, index) => {
        const band =
          row.orGroup != null
            ? {
                backgroundColor: colors.grey100,
                paddingBlock: 1,
                // Close the row gap inside a group so it reads as one block.
                marginTop: row.orGroup === 'next' ? -2 : 0,
              }
            : {};
        return (
          <React.Fragment key={index}>
            <span
              css={{
                ...band,
                paddingLeft: row.orGroup != null ? 6 : 0,
                borderRadius: '4px 0 0 4px',
                color: colors.grey500,
                overflowWrap: 'anywhere',
              }}
            >
              {row.orGroup === 'next' && (
                <span
                  css={{
                    marginRight: 4,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    color: colors.grey600,
                  }}
                >
                  {t('or')}
                </span>
              )}
              {row.label}
            </span>
            <span
              title={row.full}
              css={{
                ...band,
                paddingLeft: 12,
                paddingRight: row.orGroup != null ? 6 : 0,
                borderRadius: '0 4px 4px 0',
                color: colors.grey800,
                overflowWrap: 'anywhere',
                whiteSpace: 'pre-wrap',
              }}
            >
              {row.value}
              {row.copyable === true && <CopyButton text={row.value} />}
            </span>
          </React.Fragment>
        );
      })}
    </div>
  );
};

const CopyButton = ({ text }: { text: string }) => {
  const { t } = useLocale();
  const [copied, setCopied] = React.useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard unavailable (permissions): the value stays selectable.
    }
  };
  return (
    <Tooltip title={copied ? t('Copied') : t('Copy')}>
      <IconButton
        size="small"
        onClick={copy}
        aria-label={t('Copy')}
        sx={{ padding: '2px', marginLeft: 0.5, verticalAlign: 'middle' }}
      >
        {copied ? <Check size={12} /> : <Copy size={12} />}
      </IconButton>
    </Tooltip>
  );
};

const TableView = ({
  block,
  colors,
}: {
  block: Extract<ReadableBlock, { kind: 'table' }>;
  colors: Colors;
}) => (
  <div>
    {block.title != null && <BlockTitle title={block.title} colors={colors} />}
    <table
      css={{
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: '0.8125rem',
        lineHeight: 1.5,
        '& th, & td': {
          padding: '2px 8px 2px 0',
          textAlign: 'left',
          verticalAlign: 'top',
          overflowWrap: 'anywhere',
        },
        '& th': { color: colors.grey500, fontWeight: 600 },
        '& td': { color: colors.grey800 },
      }}
    >
      <thead>
        <tr>
          {block.columns.map((column, index) => (
            <th key={index}>{column}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {block.rows.map((row, rowIndex) => (
          <tr key={rowIndex}>
            {row.map((cell, cellIndex) => (
              <td key={cellIndex}>{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const ChecksView = ({
  block,
  colors,
}: {
  block: Extract<ReadableBlock, { kind: 'checks' }>;
  colors: Colors;
}) => (
  <div css={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
    {block.items.map((item, index) => (
      <div
        key={index}
        css={{
          display: 'flex',
          gap: 6,
          alignItems: 'flex-start',
          fontSize: '0.8125rem',
          lineHeight: 1.5,
        }}
      >
        <span css={{ flexShrink: 0, marginTop: 2 }}>
          {item.ok ? (
            <CircleCheck size={14} color={colors.green600} />
          ) : (
            <CircleX size={14} color={colors.errorText} />
          )}
        </span>
        <span css={{ minWidth: 0, overflowWrap: 'anywhere' }}>
          <span css={{ color: colors.grey700, fontWeight: 500 }}>
            {item.label}
          </span>
          {item.detail !== '' && (
            <span css={{ color: colors.grey500 }}> — {item.detail}</span>
          )}
        </span>
      </div>
    ))}
  </div>
);

const Chip = ({
  children,
  title,
  colors,
}: {
  children: React.ReactNode;
  title?: string;
  colors: Colors;
}) => (
  <span
    title={title}
    css={{
      padding: '0 6px',
      border: `1px solid ${colors.divider}`,
      borderRadius: 10,
      fontSize: '0.72rem',
      lineHeight: '18px',
      color: colors.grey700,
      whiteSpace: 'nowrap',
    }}
  >
    {children}
  </span>
);

const MONOSPACE = "'Fira Code', 'Consolas', 'Monaco', monospace";

// Terminal look: dark background, a "$" prompt, the command verbatim.
const CommandView = ({ text, colors }: { text: string; colors: Colors }) => (
  <pre
    css={{
      margin: 0,
      padding: '8px 10px',
      backgroundColor: colors.grey900,
      color: colors.white,
      borderRadius: 6,
      fontSize: '0.75rem',
      lineHeight: 1.5,
      fontFamily: MONOSPACE,
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
    }}
  >
    <span css={{ color: colors.grey500, userSelect: 'none' }}>$ </span>
    {text}
  </pre>
);

const BadgesView = ({
  block,
  colors,
}: {
  block: Extract<ReadableBlock, { kind: 'badges' }>;
  colors: Colors;
}) => (
  <div css={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
    {block.items.map((item, index) => {
      const color =
        item.tone === 'success'
          ? colors.green600
          : item.tone === 'error'
          ? colors.errorText
          : colors.grey700;
      const NetworkIcon =
        item.icon === 'online'
          ? Wifi
          : item.icon === 'offline'
          ? WifiOff
          : null;
      const badge = (
        <span
          css={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            padding: '0 6px',
            border: `1px solid ${
              item.tone === 'neutral' ? colors.divider : color
            }`,
            borderRadius: 10,
            fontSize: '0.72rem',
            lineHeight: '18px',
            fontWeight: 500,
            color,
            whiteSpace: 'nowrap',
          }}
        >
          {NetworkIcon != null && <NetworkIcon size={11} />}
          {item.label}
        </span>
      );
      return item.hint != null ? (
        <Tooltip key={index} title={item.hint}>
          {badge}
        </Tooltip>
      ) : (
        <React.Fragment key={index}>{badge}</React.Fragment>
      );
    })}
  </div>
);

// Collapsed by default: a command's output can be long and is rarely what the
// user needs first.
const CollapsibleTextView = ({
  title,
  text,
  colors,
}: {
  title: string;
  text: string;
  colors: Colors;
}) => {
  const [open, setOpen] = React.useState(false);
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        css={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          padding: 0,
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          font: 'inherit',
          color: colors.grey700,
        }}
      >
        <ChevronRight
          size={12}
          style={{
            transform: open ? 'rotate(90deg)' : 'none',
            transition: 'transform 0.15s ease',
            flexShrink: 0,
          }}
        />
        <Typography variant="caption" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
      </button>
      <Collapse in={open} unmountOnExit>
        <pre
          css={{
            margin: '4px 0 0',
            padding: '8px 10px',
            backgroundColor: colors.grey100,
            borderRadius: 6,
            fontSize: '0.75rem',
            lineHeight: 1.5,
            fontFamily: MONOSPACE,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {text}
        </pre>
      </Collapse>
    </div>
  );
};

const BlockView = ({
  block,
  colors,
}: {
  block: ReadableBlock;
  colors: Colors;
}) => {
  const { t } = useLocale();
  switch (block.kind) {
    case 'rows':
      return (
        <div>
          {block.title != null && (
            <BlockTitle title={block.title} colors={colors} />
          )}
          <RowsView rows={block.rows} colors={colors} />
        </div>
      );
    case 'chips':
      return (
        <div>
          {block.title != null && (
            <BlockTitle title={block.title} colors={colors} />
          )}
          <div
            css={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 2 }}
          >
            {block.items.map((item, index) => (
              <Chip key={index} title={item.hint} colors={colors}>
                {item.label}
              </Chip>
            ))}
            {block.hidden > 0 && (
              <Chip colors={colors}>
                {t('+{{count}} more', { count: block.hidden })}
              </Chip>
            )}
          </div>
        </div>
      );
    case 'links':
      return (
        <div css={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {block.items.map((item, index) => (
            <div key={index}>
              <a
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                css={{ fontSize: '0.8125rem', fontWeight: 500 }}
              >
                {item.title}
              </a>
              {item.snippet != null && (
                <Typography
                  variant="caption"
                  sx={{ display: 'block', color: colors.grey500 }}
                >
                  {item.snippet}
                </Typography>
              )}
            </div>
          ))}
        </div>
      );
    case 'note':
      return (
        <Typography variant="body2" sx={{ color: colors.grey600 }}>
          {block.text}
        </Typography>
      );
    case 'table':
      return <TableView block={block} colors={colors} />;
    case 'checks':
      return <ChecksView block={block} colors={colors} />;
    case 'command':
      return <CommandView text={block.text} colors={colors} />;
    case 'badges':
      return <BadgesView block={block} colors={colors} />;
    case 'collapsible':
      return (
        <CollapsibleTextView
          title={block.title}
          text={block.text}
          colors={colors}
        />
      );
    case 'text':
      return (
        <pre
          css={{
            margin: 0,
            padding: '8px 10px',
            backgroundColor: colors.grey100,
            borderRadius: 6,
            fontSize: '0.75rem',
            lineHeight: 1.5,
            fontFamily: "'Fira Code', 'Consolas', 'Monaco', monospace",
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {block.text}
        </pre>
      );
  }
};

export const ReadableBlocks = ({
  blocks,
  colors,
}: {
  blocks: Array<ReadableBlock>;
  colors: Colors;
}) => (
  <div
    css={{
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
      marginTop: 4,
      maxHeight: 280,
      overflow: 'auto',
    }}
  >
    {blocks.map((block, index) => (
      <BlockView key={index} block={block} colors={colors} />
    ))}
  </div>
);
